import { Controller, useFormContext } from 'react-hook-form'
import { formatInTimeZone } from 'date-fns-tz'
import { getLocalTimezone } from 'lib/time'
import { useAssignmentShifts } from 'queries/assignment/useAssignmentShifts'
import { getTimezoneFromLocation } from 'lib/time/getTimezoneFromLocation'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Text,
  Table,
  Input,
} from '@workwhile/ui'

interface RequestWorkersStepProps {
  assignmentId: number
  selectedShifts: string[]
  isLoading: boolean
  onSubmit: () => void
}

export const RequestWorkersStep = ({
  assignmentId,
  selectedShifts,
  isLoading,
  onSubmit,
}: RequestWorkersStepProps) => {
  const { data: shiftsData } = useAssignmentShifts(assignmentId)
  const selectedShiftData = shiftsData?.shifts.filter((shift) =>
    selectedShifts.includes(shift.id.toString())
  )

  const firstShift = selectedShiftData?.[0]
  const timezone =
    getTimezoneFromLocation(firstShift?.location) || getLocalTimezone()

  // Use form context from FormProvider
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext()

  // Watch the numWorkers field to use in the table
  const numWorkers = watch('numWorkers') || 0

  return (
    <Box>
      <Heading level={2} mb={4}>
        Request Workers
      </Heading>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Field
          label="Number of Additional Workers"
          error={errors?.numWorkers?.message as string | undefined}
        >
          <Controller
            name="numWorkers"
            control={control}
            render={({ field }) => (
              <Input
                type="number"
                aria-label="Number of Workers"
                placeholder="Enter number of workers"
                value={field.value.toString()}
                onChange={(e) => {
                  const value = Number(e.target.value)
                  field.onChange(value)
                }}
              />
            )}
          />
        </Field>

        <Box mb={4}>
          <Text mb={3}>{selectedShifts.length} shift(s) selected</Text>

          <Table
            columns={[
              {
                header: 'Shift',
                accessorKey: 'shift',
              },
              {
                header: 'Current Workers',
                accessorKey: 'currentWorkers',
              },
              {
                header: 'Proposed Change',
                accessorKey: 'change',
              },
            ]}
            data={
              selectedShiftData?.map((shift) => ({
                shift: `${formatInTimeZone(
                  new Date(shift.startsAt),
                  timezone,
                  'EEE, MMM d, yyyy'
                )} ${formatInTimeZone(
                  new Date(shift.startsAt),
                  timezone,
                  'h:mm a'
                )} - ${formatInTimeZone(
                  new Date(shift.endsAt),
                  timezone,
                  'h:mm a (zzz)'
                )}`,
                currentWorkers: `${shift.numWorkersFilled} / ${shift.workersNeeded}`,
                change: `${shift.numWorkersFilled} / ${
                  shift.workersNeeded + numWorkers
                }`,
              })) || []
            }
          />
        </Box>

        <Flex justifyContent="flex-end">
          <Button
            onClick={onSubmit}
            disabled={numWorkers === 0 || isLoading}
            loading={isLoading}
          >
            Confirm Changes
          </Button>
        </Flex>
      </form>
    </Box>
  )
}
